import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Input } from '../../components/common'
import { useAuthStore } from '../../store/auth.store'

export const ResetPasswordForm: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { resetPassword, error, isLoading, clearError } = useAuthStore()

  const emailFromQuery = searchParams.get('email') || ''

  const [formData, setFormData] = useState({
    email: emailFromQuery,
    code: '',
    newPassword: '',
    confirmPassword: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSuccess, setIsSuccess] = useState(false)

  // Success bo'lgandan keyin avtomatik ravishda login sahifasiga o'tkazish
  useEffect(() => {
    if (isSuccess) {
      // 3 soniyadan keyin login sahifasiga avtomatik o'tkazish
      const timer = setTimeout(() => {
        navigate('/login', {
          replace: true,
          state: {
            showResetSuccess: true,
            email: formData.email,
          },
        })
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [isSuccess, navigate, formData.email])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Email validation
    if (!formData.email) {
      newErrors.email = t('validation.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid')
    }

    // Code validation
    if (!formData.code) {
      newErrors.code = t('validation.codeRequired')
    } else if (!/^\d{6}$/.test(formData.code)) {
      newErrors.code = t('validation.codeInvalid')
    }

    // Password validation
    if (!formData.newPassword) {
      newErrors.newPassword = t('validation.passwordRequired')
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t('validation.passwordMinLength')
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = t('validation.passwordComplexity')
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.confirmPasswordRequired')
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordsDontMatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear field error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }

    // Clear global error
    if (error) {
      clearError()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await resetPassword(formData)
      setIsSuccess(true)
    } catch (err) {
      // Error is handled by the store
    }
  }

  const handleBackToForgotPassword = () => {
    navigate('/forgot-password')
  }

  // Avtomatik login sahifasiga o'tishni o'tkazib yuborish va darhol login sahifasiga o'tish
  const handleGoToLoginNow = () => {
    navigate('/login', {
      replace: true,
      state: {
        showResetSuccess: true,
        email: formData.email,
      },
    })
  }

  if (isSuccess) {
    return (
      <div className="auth-form">
        <h2 className="auth-form__title">{t('auth.resetPassword')}</h2>

        <div className="auth-form__success" role="alert">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-circle-check"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginRight: '8px', verticalAlign: 'middle' }}
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <circle cx="12" cy="12" r="9" />
            <path d="M9 12l2 2l4 -4" />
          </svg>
          {t('auth.passwordResetSuccess')}
        </div>

        <div className="auth-form__info">
          <p>{t('auth.canLoginWithNewPassword')}</p>
          <p style={{ marginTop: '8px', fontSize: '0.9rem' }}>
            <strong>{t('auth.note')}:</strong> {t('auth.redirectingToLogin')}
          </p>
        </div>

        <Button
          onClick={handleGoToLoginNow}
          variant="primary"
          fullWidth
          style={{ marginTop: '16px' }}
        >
          {t('auth.goToLoginNow')}
        </Button>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <div className="auth-form__redirect-countdown">
            {t('auth.redirectingIn')}{' '}
            <span className="countdown-number">3</span> {t('common.seconds')}
          </div>
        </div>

        <p className="auth-form__footer">
          {t('auth.loginProblem')}{' '}
          <a href="/support" className="auth-form__link">
            {t('auth.contactSupport')}
          </a>
        </p>
      </div>
    )
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <h2 className="auth-form__title">{t('auth.resetPassword')}</h2>

      <p className="auth-form__description">{t('auth.enterResetDetails')}</p>

      {error && (
        <div className="auth-form__error" role="alert">
          {error}
        </div>
      )}

      <Input
        label={t('auth.email')}
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        placeholder={t('auth.enterEmail')}
        autoComplete="email"
        required
        disabled={!!emailFromQuery}
      />

      <Input
        label={t('auth.verificationCode')}
        name="code"
        value={formData.code}
        onChange={handleChange}
        error={errors.code}
        placeholder={t('auth.enterVerificationCode')}
        maxLength={6}
        required
        autoComplete="one-time-code"
      />

      <Input
        label={t('auth.newPassword')}
        type="password"
        name="newPassword"
        value={formData.newPassword}
        onChange={handleChange}
        error={errors.newPassword}
        placeholder={t('auth.enterNewPassword')}
        autoComplete="new-password"
        required
      />

      <Input
        label={t('auth.confirmPassword')}
        type="password"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleChange}
        error={errors.confirmPassword}
        placeholder={t('auth.confirmNewPassword')}
        autoComplete="new-password"
        required
      />

      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <Button
          type="button"
          onClick={handleBackToForgotPassword}
          variant="secondary"
          fullWidth
        >
          {t('common.back')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
          fullWidth
        >
          {isLoading ? t('auth.resetting') : t('auth.resetPassword')}
        </Button>
      </div>

      <p className="auth-form__footer">
        {t('auth.didntReceiveCode')}{' '}
        <a href="/forgot-password" className="auth-form__link">
          {t('auth.sendAgain')}
        </a>
      </p>
    </form>
  )
}
